import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Warning, SignOut, Buildings, Users, Gear, ChartBar, Eye,
  FileText, Clock, ShieldCheck, Trash, ChatCircleText, MapPin, MagnifyingGlass, VideoCameraSlash
} from '@phosphor-icons/react';

const statusColors = {
  'Yeni': 'bg-[hsl(var(--primary))] text-white',
  'İnceleniyor': 'bg-[hsl(var(--accent))] text-black',
  'İşleme Alındı': 'bg-blue-600 text-white',
  'Kapatıldı': 'bg-[hsl(var(--success))] text-white',
};

export default function AdminDashboard() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [instFilter, setInstFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [cities, setCities] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(val.length >= 3 ? val : '');
    }, 300);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, instRes, citiesRes] = await Promise.all([
        authAxios().get('/admin/analytics'),
        authAxios().get('/institutions/all'),
        authAxios().get('/reports/cities')
      ]);
      setAnalytics(analyticsRes.data);
      setInstitutions(instRes.data);
      setCities(citiesRes.data);
    } catch {
      toast.error('Veri yüklenemedi');
    }
    setLoading(false);
  }, [authAxios]);

  const fetchReports = useCallback(async () => {
    const params = { page: 1, limit: 10 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (instFilter !== 'all') params.institution_id = instFilter;
    if (cityFilter !== 'all') params.city = cityFilter;
    if (searchQuery) params.search = searchQuery;
    try {
      const res = await authAxios().get('/reports', { params });
      setReports(res.data.reports);
    } catch {}
  }, [authAxios, statusFilter, instFilter, cityFilter, searchQuery]);

  const handleDeleteVideo = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm('Bu ihbarın videosunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;
    setDeletingId(reportId);
    try {
      await authAxios().delete(`/reports/${reportId}/video`);
      toast.success('Video silindi');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video silinemedi');
    } finally { setDeletingId(null); }
  };

  const handleDeleteReport = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm('Bu ihbar kaydını tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;
    setDeletingId(reportId);
    try {
      await authAxios().delete(`/reports/${reportId}`);
      toast.success('İhbar kaydı silindi');
      fetchData();
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme başarısız');
    } finally { setDeletingId(null); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck weight="fill" className="w-6 h-6 text-[hsl(var(--primary))]" />
          <span className="font-display text-xl text-white">YÖNETİM</span>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="nav-institutions" variant="ghost" onClick={() => navigate('/admin/institutions')} className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10 text-xs">
            <Buildings weight="bold" className="w-4 h-4 mr-1" /><span className="hidden md:inline">Kurumlar</span>
          </Button>
          <Button data-testid="nav-users" variant="ghost" onClick={() => navigate('/admin/users')} className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10 text-xs">
            <Users weight="bold" className="w-4 h-4 mr-1" /><span className="hidden md:inline">Kullanıcılar</span>
          </Button>
          <Button data-testid="nav-settings" variant="ghost" onClick={() => navigate('/admin/settings')} className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10 text-xs">
            <Gear weight="bold" className="w-4 h-4 mr-1" /><span className="hidden md:inline">Ayarlar</span>
          </Button>
          <Button data-testid="logout-button" variant="ghost" onClick={() => { logout(); navigate('/login'); }} className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10">
            <SignOut weight="bold" className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="p-4 max-w-6xl mx-auto">
        {/* Analytics Cards - Bento Grid */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardContent className="p-4">
                <p className="overline text-[hsl(var(--muted-foreground))]">Toplam İhbar</p>
                <p className="font-display text-3xl text-white mt-1">{analytics.total_reports}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardContent className="p-4">
                <p className="overline text-[hsl(var(--muted-foreground))]">Bugün</p>
                <p className="font-display text-3xl text-[hsl(var(--primary))] mt-1">{analytics.today_count}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardContent className="p-4">
                <p className="overline text-[hsl(var(--muted-foreground))]">Bu Hafta</p>
                <p className="font-display text-3xl text-[hsl(var(--accent))] mt-1">{analytics.week_count}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardContent className="p-4">
                <p className="overline text-[hsl(var(--muted-foreground))]">Aktif Kullanıcı</p>
                <p className="font-display text-3xl text-[hsl(var(--success))] mt-1">{analytics.active_users}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status breakdown */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(analytics.by_status).map(([status, count]) => (
              <Card key={status} className="bg-[#1C1C1E] border-[#2C2C2E]">
                <CardContent className="p-3 flex items-center justify-between">
                  <Badge className={`${statusColors[status] || 'bg-[#2C2C2E] text-white'} text-xs`}>{status}</Badge>
                  <span className="font-display text-xl text-white">{count}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Institution breakdown */}
        {analytics && Object.keys(analytics.by_institution).length > 0 && (
          <Card className="bg-[#1C1C1E] border-[#2C2C2E] mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
                <ChartBar weight="bold" className="w-4 h-4" /> KURUMLARA GÖRE
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(analytics.by_institution).map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between">
                    <span className="text-sm text-white">{name}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-[#2C2C2E] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[hsl(var(--primary))] rounded-full"
                          style={{ width: `${analytics.total_reports > 0 ? (count / analytics.total_reports * 100) : 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-[hsl(var(--muted-foreground))] w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Reports */}
        <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
                <FileText weight="bold" className="w-4 h-4" /> SON İHBARLAR
                <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-bold font-body">{reports.length}</span>
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <MagnifyingGlass weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <Input
                    data-testid="search-input"
                    value={searchTerm}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="Ara... (min 3 karakter)"
                    className="h-8 w-44 pl-8 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666] text-xs"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36 h-8 bg-[#0A0A0A] border-[#2C2C2E] text-white text-xs">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                    <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Tümü</SelectItem>
                    <SelectItem value="Yeni" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Yeni</SelectItem>
                    <SelectItem value="İnceleniyor" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">İnceleniyor</SelectItem>
                    <SelectItem value="İşleme Alındı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">İşleme Alındı</SelectItem>
                    <SelectItem value="Kapatıldı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Kapatıldı</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={instFilter} onValueChange={setInstFilter}>
                  <SelectTrigger className="w-36 h-8 bg-[#0A0A0A] border-[#2C2C2E] text-white text-xs">
                    <SelectValue placeholder="Kurum" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                    <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Tüm Kurumlar</SelectItem>
                    {institutions.map(i => (
                      <SelectItem key={i.id} value={i.id} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cities.length > 0 && (
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger data-testid="city-filter" className="w-36 h-8 bg-[#0A0A0A] border-[#2C2C2E] text-white text-xs">
                      <SelectValue placeholder="Şehir" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                      <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Tüm Şehirler</SelectItem>
                      {cities.map(c => (
                        <SelectItem key={c} value={c} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <p className="text-center py-6 text-[hsl(var(--muted-foreground))]">İhbar bulunamadı</p>
            ) : (
              <div className="space-y-2">
                {reports.map(r => (
                  <div
                    key={r.id}
                    className="p-3 rounded-lg border border-[#2C2C2E] hover:border-[#3C3C3E] transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/report/${r.id}`)}
                    data-testid={`admin-report-${r.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-heading">{r.report_no}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{r.institution_name} - {formatDate(r.created_at)}</p>
                        {r.reporter_name && <p className="text-xs text-[hsl(var(--accent))]">{r.reporter_name}</p>}
                      </div>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <Badge className={`${statusColors[r.status] || 'bg-[#2C2C2E] text-white'} text-xs`}>{r.status}</Badge>
                        {!r.video_deleted && (
                          <button
                            data-testid={`delete-video-${r.id}`}
                            onClick={(e) => handleDeleteVideo(e, r.id)}
                            disabled={deletingId === r.id}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-orange-400 hover:bg-orange-400/10 transition-colors"
                            title="Videoyu Sil"
                          >
                            <VideoCameraSlash weight="bold" className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`delete-report-${r.id}`}
                          onClick={(e) => handleDeleteReport(e, r.id)}
                          disabled={deletingId === r.id}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors"
                          title="İhbarı Sil"
                        >
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {r.latitude && (
                      <div className="mt-1.5">
                        <button
                          data-testid={`location-link-${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const label = r.report_no.slice(-6);
                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                            const url = isIOS
                              ? `maps://maps.apple.com/?q=${encodeURIComponent(label)}&ll=${r.latitude},${r.longitude}&z=17`
                              : `https://www.google.com/maps?q=${r.latitude},${r.longitude}+(${label})`;
                            window.open(url, '_blank');
                          }}
                          className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:text-white transition-colors"
                        >
                          <MapPin weight="fill" className="w-3.5 h-3.5" />
                          <span className="underline underline-offset-2">{r.city || 'Konumu Gör'}</span>
                        </button>
                      </div>
                    )}
                    {r.last_note && (
                      <div className="mt-2 flex items-start gap-2 bg-[#0A0A0A] rounded-md px-3 py-2">
                        <ChatCircleText weight="fill" className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs text-[hsl(var(--accent))] font-medium">{r.last_note_status}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{r.last_note}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
